<template>
  <section class="sp">
    <h3 class="sp-heading">Ready to accept this plan?</h3>
    <p class="sp-intro">
      Sign below to confirm you've reviewed the treatment plan. Your signed copy will be sent
      to your dental office and emailed to you (if you've shared an email with them).
    </p>

    <!-- Office disclaimer (Settings → Presentation → Disclaimer & acknowledgements).
         ONE panel serves Present Mode and the patient link, so both get the same
         lines in the same order: checkboxes, then the paragraph, then the signature.
         Whole <label> is the tap target (chairside iPad). The paragraph is plain text
         with its line breaks kept (pre-line) and no inner scroll box. -->
    <template v-if="disclaimer">
      <div v-if="officeLines" class="sp-acks">
        <label
          v-for="ack in disclaimer.acknowledgements"
          :key="ack.id"
          class="sp-ack"
          :class="{ 'sp-ack-on': !!ackChecked[ack.id] }"
          data-test="sp-ack"
        >
          <input
            type="checkbox"
            :checked="!!ackChecked[ack.id]"
            :disabled="busy"
            data-test="sp-ack-check"
            @change="onToggleAck(ack.id, $event.target.checked)"
          />
          <span class="sp-ack-text">{{ ack.text }}</span>
          <span v-if="ack.required !== false" class="sp-ack-req">Required</span>
        </label>
      </div>
      <div v-if="disclaimer.text" class="sp-disclaimer" data-test="sp-disclaimer">{{ disclaimer.text }}</div>
    </template>

    <div v-if="!hideName" class="sp-field">
      <label class="sp-label">Type your full name</label>
      <input
        v-model="typedName"
        type="text"
        class="sp-input"
        placeholder="Your full legal name"
        :disabled="busy"
      />
    </div>

    <div class="sp-field">
      <label class="sp-label">Sign with your finger or mouse</label>
      <div class="sp-pad-wrap" :class="{ 'sp-pad-empty': isPadEmpty && !busy }">
        <VPerfectSignature
          ref="pad"
          class="sp-pad"
          :stroke-options="strokeOptions"
          :pen-color="'#0f172a'"
          :will-read-frequently="true"
          @onEnd="onStrokeEnd"
        />
        <button v-if="!isPadEmpty" class="sp-clear" type="button" @click="onClear" :disabled="busy">
          <i class="fa fa-times"></i> Clear
        </button>
      </div>
    </div>

    <!-- Teamio's built-in confirmation. Replaced by the office's own lines when it has
         any (the office's checked lines are the record instead). -->
    <label v-if="!officeLines" class="sp-consent" data-test="sp-consent">
      <input type="checkbox" v-model="consentChecked" :disabled="busy" />
      <span>
        I confirm I've reviewed the treatment plan, and I authorize my dental office to receive
        a signed copy.
      </span>
    </label>

    <div v-if="errorText" class="sp-error">{{ errorText }}</div>

    <button
      class="sp-submit"
      :disabled="!canSubmit"
      @click="onSubmit"
    >
      <i v-if="busy" class="fa fa-circle-o-notch fa-spin"></i>
      <i v-else class="fa fa-paper-plane"></i>
      {{ busy ? busyLabel : 'Sign &amp; send to office' }}
    </button>

    <p class="sp-note">
      We'll never share your information with anyone outside your dental office.
    </p>
  </section>
</template>

<script>
import VPerfectSignature from 'v-perfect-signature';
import { hasOfficeLines, missingRequiredAcks } from '@/pages/TxPresenter/utils/disclaimer.js';

export default {
  name: 'SignaturePanel',
  components: { VPerfectSignature },
  props: {
    busy: { type: Boolean, default: false },
    busyLabel: { type: String, default: 'Sending…' },
    error: { type: String, default: '' },
    // Present Mode (in-person) hides the typed-name field — the patient is
    // signing on the device chairside, so the name is known (`defaultName`).
    // At-home keeps the field as the legal attestation.
    hideName: { type: Boolean, default: false },
    defaultName: { type: String, default: '' },
    // The office's insurance disclaimer, already resolved by
    // resolvePatientDisclaimer(): { text, acknowledgements: [{ id, text, required }] }
    // or null when the office has none (then this panel is exactly the original).
    // Present Mode resolves it from the loaded settings; the patient link gets it
    // from /get-tx-patient-view.
    disclaimer: { type: Object, default: null },
  },
  data() {
    return {
      typedName: '',
      consentChecked: false,
      // Which of the office's acknowledgement lines are ticked, keyed by line id.
      // Written with $set so a not-yet-present key is reactive.
      ackChecked: {},
      isPadEmpty: true,
      strokeOptions: {
        size: 6,
        thinning: 0.5,
        smoothing: 0.3,
        streamline: 0.3,
      },
    };
  },
  computed: {
    // D2: with ≥1 office line the built-in "I confirm I've reviewed…" checkbox is
    // hidden (and no longer required) - the office's own lines replace it.
    officeLines() {
      return hasOfficeLines(this.disclaimer);
    },
    // Ticked line ids in display order - what the server checks (400 when a
    // required one is missing) and freezes into the signed record.
    acknowledgedIds() {
      const lines = (this.disclaimer && this.disclaimer.acknowledgements) || [];
      return lines.filter((a) => this.ackChecked[a.id]).map((a) => a.id);
    },
    canSubmit() {
      if (this.busy) return false;
      if (!this.hideName && !this.typedName.trim()) return false;
      if (this.isPadEmpty) return false;
      if (!this.officeLines && !this.consentChecked) return false;
      if (missingRequiredAcks(this.disclaimer, this.acknowledgedIds).length > 0) return false;
      return true;
    },
    errorText() {
      return this.error || '';
    },
  },
  watch: {
    // A new disclaimer object (settings reloaded, patient link refetched after a
    // signature) must never inherit ticks from the old one - ids can be reused.
    disclaimer() {
      this.ackChecked = {};
    },
  },
  methods: {
    onToggleAck(id, checked) {
      this.$set(this.ackChecked, id, !!checked);
    },
    onStrokeEnd() {
      this.isPadEmpty = false;
    },
    onClear() {
      if (this.$refs.pad) this.$refs.pad.clear();
      this.isPadEmpty = true;
    },
    /**
     * Returns the signature as a PNG data URL, or null if empty.
     * Used by the parent during submit to embed the signature into the PDF.
     */
    getSignatureDataUrl() {
      if (!this.$refs.pad || this.isPadEmpty) return null;
      try {
        return this.$refs.pad.toDataURL('image/png');
      } catch (e) {
        console.warn('[SignaturePanel] toDataURL failed:', e.message);
        return null;
      }
    },
    onSubmit() {
      if (!this.canSubmit) return;
      const sigDataUrl = this.getSignatureDataUrl();
      if (!sigDataUrl) {
        this.$emit('error', 'Could not capture your signature. Try drawing again.');
        return;
      }
      const signerName = this.hideName
        ? (this.defaultName || 'Patient').trim()
        : this.typedName.trim();
      this.$emit('submit', {
        signerName,
        signatureDataUrl: sigDataUrl,
        acknowledgedIds: this.acknowledgedIds,
      });
    },
  },
};
</script>

<style scoped>
.sp {
  margin-top: 32px;
  padding: 24px 28px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.sp * { box-sizing: border-box; }

.sp-heading {
  margin: 0 0 6px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 20px;
  color: #0d5f54;
  font-weight: 600;
}
.sp-intro {
  margin: 0 0 18px;
  font-size: 13px;
  color: #475569;
  line-height: 1.55;
}

/* Office acknowledgement lines. Sized for a chairside iPad: 14px+ text, a 22px
   checkbox, and the whole row is the <label> so any tap on it toggles. */
.sp-acks {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 16px;
}
.sp-ack {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #1e293b;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.sp-ack-on {
  background: #f0faf8;
  border-color: #0d5f54;
}
.sp-ack input[type="checkbox"] {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin: 0;
  accent-color: #0d5f54;
  cursor: pointer;
}
.sp-ack-text {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.sp-ack-req {
  flex-shrink: 0;
  margin-top: 2px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 999px;
}
/* The office's fine print: plain text, line breaks kept, no inner scroll box. */
.sp-disclaimer {
  margin: 0 0 18px;
  padding: 12px 14px;
  background: #f8fafc;
  border-left: 3px solid #cbd5e1;
  border-radius: 0 6px 6px 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: #334155;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.sp-field { margin-bottom: 16px; }
.sp-label {
  display: block;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.sp-input {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  color: #0f172a;
  background: #fff;
}
.sp-input:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}

.sp-pad-wrap {
  position: relative;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #fafbfc;
  height: 160px;
  overflow: hidden;
  transition: border-color 0.15s, background 0.15s;
}
.sp-pad-empty {
  background: repeating-linear-gradient(
    45deg, #fafbfc, #fafbfc 8px, #f7f8fa 8px, #f7f8fa 16px
  );
}
.sp-pad-wrap:focus-within {
  border-color: #0d5f54;
  background: #fff;
}
.sp-pad {
  width: 100%;
  height: 100%;
  display: block;
}
.sp-clear {
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 11px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e4e7ec;
  border-radius: 5px;
  color: #475569;
  cursor: pointer;
  font-family: inherit;
}
.sp-clear:hover:not(:disabled) {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}
.sp-clear:disabled { opacity: 0.5; cursor: not-allowed; }

.sp-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 18px 0;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
  cursor: pointer;
}
.sp-consent input[type="checkbox"] {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #0d5f54;
  cursor: pointer;
}

.sp-error {
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #b91c1c;
  font-size: 13px;
}

.sp-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  background: #0d5f54;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}
.sp-submit:hover:not(:disabled) { background: #094840; }
.sp-submit:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.sp-note {
  margin: 12px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  text-align: center;
  line-height: 1.5;
}

@media (max-width: 600px) {
  .sp { padding: 18px 16px; }
  .sp-heading { font-size: 18px; }
  .sp-pad-wrap { height: 130px; }
}
</style>
